import {
  Box,
  Button,
  ButtonProps,
  Grid,
  GridColumn,
  IconNameType,
  Link,
  Text,
  ThemeSizes
} from "@modernatx/ui-kit-react";
import React from "react";

export interface SummaryBannerProps {
  label: string | React.ReactNode;
  amount: string | React.ReactNode;
  credits: string;
  onClickCreditsLink?: () => void;
  actions?: {
    label: string;
    onClick: () => void;
    variant?: ButtonProps["variant"];
    icon?: IconNameType;
  }[];
  showError?: boolean;
}

export const SummaryBanner: React.FC<SummaryBannerProps> = ({
  label,
  amount,
  credits,
  onClickCreditsLink,
  actions,
  showError
}) => {
  const renderBalance = React.useCallback(() => {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          gap: "spacing-3"
        }}
      >
        <Text variant="body-1-regular">{label}</Text>

        <Text variant="h2" sx={{ color: "text-emphasis" }}>
          {showError ? (
            <Text variant="body-1-regular">Error loading balance</Text>
          ) : (
            `${amount}*`
          )}
        </Text>

        <Text variant="body-1-regular">
          {`*includes ${credits && `${credits} of `}`}
          {onClickCreditsLink ? (
            <Link onClick={onClickCreditsLink}>credits</Link>
          ) : (
            <Text>credits</Text>
          )}
          ; pending payments are not reflected
        </Text>
      </Box>
    );
  }, [label, showError, amount, credits, onClickCreditsLink]);

  return (
    <Grid>
      <GridColumn fillGapEnd={true} size={[4, 4, 8]}>
        <Box
          sx={{
            backgroundColor: "surface-neutral-default",
            borderColor: "stroke-neutral-secondary",
            borderEndStartRadius: "radius-2",
            borderStartStartRadius: "radius-2",
            borderStyle: "solid",
            borderWidth: "1px 0 1px 1px",
            boxSizing: "border-box",
            boxShadow: "shadow-100",
            display: ["none", "flex"],
            flex: 1,
            flexDirection: "column",
            gap: ["spacing-4", "spacing-none"],
            justifyContent: "center",
            p: "spacing-8"
          }}
        >
          {renderBalance()}
        </Box>
      </GridColumn>
      <GridColumn size={4}>
        <Box
          sx={{
            backgroundColor: "surface-neutral-default",
            borderColor: "stroke-neutral-secondary",
            borderEndEndRadius: "radius-2",
            borderStartEndRadius: "radius-2",
            borderEndStartRadius: ["radius-2", "unset", "unset"],
            borderStartStartRadius: ["radius-2", "unset", "unset"],
            borderStyle: "solid",
            borderWidth: "1px 1px 1px 0",
            boxShadow: "shadow-100",
            display: "flex",
            flex: 1,
            flexDirection: "column",
            gap: "spacing-4",
            justifyContent: "center",
            p: "spacing-8"
          }}
        >
          <Box sx={{ display: ["flex", "none"], pb: 4 }}>{renderBalance()}</Box>

          {!!actions?.length && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: ["none", "none", "flex-end"],
                gap: "spacing-4"
              }}
            >
              {actions.map((action, index) => (
                <Button
                  key={index}
                  variant={action.variant || "filled"}
                  onClick={action.onClick}
                  icon={action.icon}
                  iconPosition="end"
                  sx={{
                    width: "100%",
                    maxWidth: "290px" as ThemeSizes
                  }}
                >
                  {action.label}
                </Button>
              ))}
            </Box>
          )}
        </Box>
      </GridColumn>
    </Grid>
  );
};
